/**
 * Calendly on our own site instead of a hard redirect to calendly.com.
 *
 * Why: a click on "Termin vereinbaren" is not a booking. Only when Calendly
 * runs embedded on our page does it tell us (via postMessage) that a meeting
 * was actually scheduled, and only then can that booking be attributed to the
 * Google Ads click that brought the visitor here.
 *
 * The widget script is loaded lazily on the first click, so visitors who never
 * open the calendar never contact Calendly.
 *
 * Docs: https://help.calendly.com/hc/en-us/articles/360020052833
 */

const CALENDLY_ORIGIN = 'https://calendly.com';
const WIDGET_JS = 'https://assets.calendly.com/assets/external/widget.js';
const WIDGET_CSS = 'https://assets.calendly.com/assets/external/widget.css';

// If the widget is not ready by then (blocked, offline, slow), the popup falls
// back to opening calendly.com directly so the button never "does nothing".
const POPUP_TIMEOUT_MS = 4000;
// The /demo page waits longer: there the calendar is the whole point and the
// script competes with the rest of the page on slow mobile connections.
const INLINE_TIMEOUT_MS = 15000;

let widgetPromise = null;

const loadScript = () =>
  new Promise((resolve, reject) => {
    if (window.Calendly) {
      resolve(window.Calendly);
      return;
    }
    let script = document.querySelector(`script[src="${WIDGET_JS}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = WIDGET_JS;
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => {
      if (window.Calendly) resolve(window.Calendly);
      else reject(new Error('Calendly widget missing after load'));
    });
    script.addEventListener('error', () => {
      // Remove the dead tag so a later click starts a fresh attempt.
      script.remove();
      reject(new Error('Calendly widget failed to load'));
    });
  });

const loadStylesheet = () =>
  new Promise((resolve, reject) => {
    let link = document.querySelector(`link[href="${WIDGET_CSS}"]`);
    if (link && link.dataset.loaded === 'true') {
      resolve();
      return;
    }
    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = WIDGET_CSS;
      document.head.appendChild(link);
    }
    link.addEventListener('load', () => {
      link.dataset.loaded = 'true';
      resolve();
    });
    link.addEventListener('error', () => {
      link.remove();
      reject(new Error('Calendly stylesheet failed to load'));
    });
  });

const withTimeout = (promise, ms) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('Calendly widget timed out')),
      ms
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

/**
 * Loads Calendly's widget script and stylesheet once. A timeout only gives up
 * for that caller; the download keeps going and later calls can still use it.
 * @param {number} [timeoutMs]
 * @returns {Promise<object>} Resolves with window.Calendly
 */
export const loadCalendlyWidget = (timeoutMs = POPUP_TIMEOUT_MS) => {
  if (!widgetPromise) {
    widgetPromise = Promise.all([loadScript(), loadStylesheet()])
      .then(([Calendly]) => Calendly)
      .catch((error) => {
        // Allow a fresh attempt on the next call.
        widgetPromise = null;
        throw error;
      });
  }
  return withTimeout(widgetPromise, timeoutMs);
};

/**
 * Opens the booking calendar as an overlay on the current page. Falls back to
 * calendly.com in the same tab (never blocked by popup blockers) if the
 * widget cannot be used.
 * @param {string} url Calendly scheduling link
 */
export const openCalendlyPopup = (url) => {
  const root = document.documentElement;
  root.style.cursor = 'progress';

  loadCalendlyWidget()
    .then((Calendly) => Calendly.initPopupWidget({ url }))
    .catch(() => window.location.assign(url))
    .finally(() => {
      root.style.cursor = '';
    });
};

/**
 * Renders the calendar inline into `parentElement`.
 * @param {string} url Calendly scheduling link
 * @param {HTMLElement} parentElement
 * @returns {Promise<void>} Rejects if the widget could not be loaded
 */
export const initCalendlyInline = (url, parentElement) =>
  loadCalendlyWidget(INLINE_TIMEOUT_MS).then((Calendly) => {
    parentElement.innerHTML = '';
    Calendly.initInlineWidget({ url, parentElement });
  });

const isCalendlyMessage = (event) =>
  event.origin === CALENDLY_ORIGIN &&
  event.data &&
  typeof event.data.event === 'string' &&
  event.data.event.indexOf('calendly.') === 0;

/**
 * Calls `callback` once per completed booking (popup or inline widget).
 * @param {(booking: { bookingId: string | null }) => void} callback
 *   `bookingId` is the invitee id from Calendly, stable per booking.
 * @returns {() => void} Unsubscribe
 */
export const onCalendlyBooking = (callback) => {
  const seen = new Set();

  const handleMessage = (event) => {
    if (!isCalendlyMessage(event)) return;
    if (event.data.event !== 'calendly.event_scheduled') return;

    const payload = event.data.payload || {};
    const uri =
      (payload.invitee && payload.invitee.uri) ||
      (payload.event && payload.event.uri) ||
      '';
    const bookingId = uri ? uri.split('/').filter(Boolean).pop() : null;

    if (bookingId) {
      if (seen.has(bookingId)) return;
      seen.add(bookingId);
    }
    callback({ bookingId });
  };

  window.addEventListener('message', handleMessage);
  return () => window.removeEventListener('message', handleMessage);
};
